import { Job } from '@applicator/shared'
import { formatNumberWithSpaces, formatSalaryForDisplay, unformatNumber } from '@applicator/shared'

import { supabase } from '../../lib/supabase'

// Position editing handlers
export function handlePositionEditStart(
  currentJob: Job | null,
  setPositionInputValue: React.Dispatch<React.SetStateAction<string>>,
  setEditingPosition: React.Dispatch<React.SetStateAction<boolean>>,
  positionInputRef: React.RefObject<HTMLInputElement>
) {
  if (currentJob) {
    setPositionInputValue(currentJob.position)
    setEditingPosition(true)
    setTimeout(() => {
      positionInputRef.current?.focus()
      positionInputRef.current?.select()
    }, 0)
  }
}

export async function handlePositionSave(
  currentJob: Job | null,
  positionInputValue: string,
  jobs: Job[],
  setJobs: React.Dispatch<React.SetStateAction<Job[]>>,
  setEditingPosition: React.Dispatch<React.SetStateAction<boolean>>,
  setError: React.Dispatch<React.SetStateAction<string | null>>
) {
  if (!currentJob || !currentJob.id || !positionInputValue.trim()) {
    setEditingPosition(false)
    return
  }

  const newTitle = positionInputValue.trim()

  // Optimistic update
  const updatedJobs = jobs.map(job => 
    job.id === currentJob.id ? { ...job, position: newTitle } : job
  )
  setJobs(updatedJobs)
  setEditingPosition(false)

  // Update in Supabase
  const { error } = await supabase
    .from('jobs')
    .update({ title: newTitle })
    .eq('id', currentJob.id)

  if (error) {
    // Revert on error
    setJobs(jobs)
    setError(error.message)
  }
}

export function handlePositionCancel(
  currentJob: Job | null,
  setEditingPosition: React.Dispatch<React.SetStateAction<boolean>>,
  setPositionInputValue: React.Dispatch<React.SetStateAction<string>>
) {
  setEditingPosition(false)
  if (currentJob) {
    setPositionInputValue(currentJob.position)
  }
}

export function handlePositionKeyDown(
  e: React.KeyboardEvent<HTMLInputElement>,
  handlePositionSave: () => void,
  handlePositionCancel: () => void
) {
  if (e.key === 'Enter') {
    handlePositionSave()
  } else if (e.key === 'Escape') {
    handlePositionCancel()
  }
}

// Company editing handlers
export function handleCompanyEditStart(
  currentJob: Job | null,
  setCompanyInputValue: React.Dispatch<React.SetStateAction<string>>,
  setEditingCompany: React.Dispatch<React.SetStateAction<boolean>>,
  companyInputRef: React.RefObject<HTMLInputElement>
) {
  if (currentJob) {
    setCompanyInputValue(currentJob.company)
    setEditingCompany(true)
    setTimeout(() => {
      companyInputRef.current?.focus()
      companyInputRef.current?.select()
    }, 0)
  }
}

export async function handleCompanySave(
  currentJob: Job | null,
  companyInputValue: string,
  jobs: Job[],
  setJobs: React.Dispatch<React.SetStateAction<Job[]>>,
  setEditingCompany: React.Dispatch<React.SetStateAction<boolean>>,
  setError: React.Dispatch<React.SetStateAction<string | null>>
) {
  if (!currentJob || !currentJob.id || !companyInputValue.trim()) {
    setEditingCompany(false)
    return
  }

  const newCompany = companyInputValue.trim()

  // Optimistic update
  const updatedJobs = jobs.map(job => 
    job.id === currentJob.id ? { ...job, company: newCompany } : job
  )
  setJobs(updatedJobs)
  setEditingCompany(false)

  // Update in Supabase
  const { error } = await supabase
    .from('jobs')
    .update({ company: newCompany })
    .eq('id', currentJob.id)

  if (error) {
    // Revert on error
    setJobs(jobs)
    setError(error.message)
  }
}

export function handleCompanyCancel(
  currentJob: Job | null,
  setEditingCompany: React.Dispatch<React.SetStateAction<boolean>>,
  setCompanyInputValue: React.Dispatch<React.SetStateAction<string>>
) {
  setEditingCompany(false)
  if (currentJob) {
    setCompanyInputValue(currentJob.company)
  }
}

export function handleCompanyKeyDown(
  e: React.KeyboardEvent<HTMLInputElement>,
  handleCompanySave: () => void,
  handleCompanyCancel: () => void
) {
  if (e.key === 'Enter') {
    handleCompanySave()
  } else if (e.key === 'Escape') {
    handleCompanyCancel()
  }
}

// Salary editing handlers
export function handleSalaryEditStart(
  currentJob: Job | null,
  setSalaryInputValue: React.Dispatch<React.SetStateAction<string>>,
  setEditingSalary: React.Dispatch<React.SetStateAction<boolean>>,
  salaryInputRef: React.RefObject<HTMLInputElement>
) {
  if (currentJob) {
    // Извлекаем число из строки типа "$0" или "$50000"
    const numericValue = currentJob.salary.replace(/\D/g, '')
    
    // Если значение равно 0, показываем пустое поле
    if (numericValue === '0' || numericValue === '') {
      setSalaryInputValue('')
    } else {
      // Форматируем с пробелами
      setSalaryInputValue(formatNumberWithSpaces(numericValue))
    }
    setEditingSalary(true)
    setTimeout(() => {
      salaryInputRef.current?.focus()
      salaryInputRef.current?.select()
    }, 0)
  }
}

export async function handleSalarySave(
  currentJob: Job | null,
  salaryInputValue: string,
  jobs: Job[],
  setJobs: React.Dispatch<React.SetStateAction<Job[]>>,
  setEditingSalary: React.Dispatch<React.SetStateAction<boolean>>,
  setError: React.Dispatch<React.SetStateAction<string | null>>
) {
  if (!currentJob || !currentJob.id) {
    setEditingSalary(false)
    return
  }

  // Убираем пробелы и форматирование перед сохранением
  const numericValue = unformatNumber(salaryInputValue)
  const salaryMax = numericValue ? parseInt(numericValue, 10) : 0
  // Форматируем с пробелами для отображения
  const formattedSalary = numericValue ? formatSalaryForDisplay(`$${numericValue}`) : '$0'

  // Optimistic update
  const updatedJobs = jobs.map(job => 
    job.id === currentJob.id ? { ...job, salary: formattedSalary } : job
  )
  setJobs(updatedJobs)
  setEditingSalary(false)

  // Update in Supabase
  const { error } = await supabase
    .from('jobs')
    .update({ 
      salary_max: salaryMax,
      salary_currency: 'USD'
    })
    .eq('id', currentJob.id)

  if (error) {
    // Revert on error
    setJobs(jobs)
    setError(error.message)
  }
}

export function handleSalaryCancel(
  currentJob: Job | null,
  setEditingSalary: React.Dispatch<React.SetStateAction<boolean>>,
  setSalaryInputValue: React.Dispatch<React.SetStateAction<string>>
) {
  setEditingSalary(false)
  if (currentJob) {
    // Восстанавливаем исходное значение
    const numericValue = currentJob.salary.replace(/\D/g, '')
    if (numericValue === '0' || numericValue === '') {
      setSalaryInputValue('')
    } else {
      setSalaryInputValue(formatNumberWithSpaces(numericValue))
    }
  }
}

export function handleSalaryKeyDown(
  e: React.KeyboardEvent<HTMLInputElement>,
  handleSalarySave: () => void,
  handleSalaryCancel: () => void
) {
  if (e.key === 'Enter') {
    handleSalarySave()
  } else if (e.key === 'Escape') {
    handleSalaryCancel()
  }
}

// Обработчик изменения значения salary input с форматированием
export function handleSalaryInputChange(
  e: React.ChangeEvent<HTMLInputElement>,
  setSalaryInputValue: React.Dispatch<React.SetStateAction<string>>
) {
  const value = e.target.value
  // Убираем все нецифровые символы, кроме пробелов (для форматирования)
  const numericValue = value.replace(/[^0-9\s]/g, '')
  // Форматируем с пробелами
  const formatted = formatNumberWithSpaces(numericValue)
  setSalaryInputValue(formatted)
}

// Excitement change handler
export async function handleExcitementChange(
  newValue: number,
  currentJob: Job | null,
  jobs: Job[],
  setJobs: React.Dispatch<React.SetStateAction<Job[]>>,
  setError: React.Dispatch<React.SetStateAction<string | null>>
) {
  if (!currentJob || !currentJob.id) return

  // Optimistic update
  const updatedJobs = jobs.map(job => 
    job.id === currentJob.id ? { ...job, excitement: newValue } : job
  )
  setJobs(updatedJobs)

  // Update in Supabase
  const { error } = await supabase
    .from('jobs')
    .update({ excitement: newValue })
    .eq('id', currentJob.id)

  if (error) {
    // Revert on error
    setJobs(jobs)
    setError(error?.message || 'Failed to update excitement')
  }
}
